'use client';

import React, { ChangeEvent, FormEvent, useState } from 'react';
import { toast } from 'react-toastify';

export default function ContactSection() {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    message: '',
  });

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [formSuccess, setFormSuccess] = useState<boolean>(false);
  const [agreed, setAgreed] = useState(false);

  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;

    setFormData((prevData) => ({ ...prevData, [name]: value }));
  };

  const resetFormData = () => {
    setFormData({
      fullName: '',
      email: '',
      message: '',
    });
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    setIsLoading(true);

    const newErrors: { [key: string]: string } = {};

    if (!formData.fullName.trim()) {
      newErrors.fullName = 'Veuillez renseigner votre Nom et Prénom.';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Veuillez renseigner votre adresse email.';
    }

    if (!formData.message.trim()) {
      newErrors.message = 'Veuillez écrire un message.';
    }

    if (!agreed) {
      newErrors.agreed = 'Vous devez accepter les conditions pour envoyer le formulaire.';
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      setIsLoading(false);
      return;
    }

    try {
      fetch('/api/sendEmail', {
        method: 'POST',
        body: JSON.stringify(formData),
        headers: {
          accept: 'application/json',
          'Content-Type': 'application/json',
        },
      }).then((response) => {
        const json = response.json();
        if (!response.ok) {
          console.log('Error occured :', json);
        } else {
          setFormData({
            fullName: '',
            email: '',
            message: '',
          });

          resetFormData();
          setFormSuccess(true);
        }

        setIsLoading(false);
      });
    } catch (error) {
      setIsLoading(false);
      console.error('Error sending email :', error);
      toast.error("Désolé, une erreur est survenue, nous n'avons pas pu envoyer votre message.", {
        position: 'top-right',
        autoClose: 3000,
      });
    }
  };

  const contactForm = () => {
    if (formSuccess) {
      return (
        <div role="alert" aria-live="polite" className="stonerwhitebg space-y-4 text-center">
          <h3>Votre message a bien été envoyé.</h3>

          <h3>À bientôt !</h3>
        </div>
      );
    }

    return (
      <>
        <form
          onSubmit={handleSubmit}
          className="gap-4 flex flex-col"
          role="form"
          aria-label="Formulaire de contact"
        >
          <div className="gap-4 flex w-full flex-col">
            <label className="text-xs md:text-sm uppercase" htmlFor="fullname">
              Nom Prénom
            </label>

            <div className="stonerwhitebg rounded-lg shadow-lg">
              <input
                id="fullname"
                type="text"
                className="md:text-md rounded-lg p-4 text-sm w-full transition-all duration-150 ease-linear focus:ring focus:outline-none"
                placeholder="Nom Prénom"
                name="fullName"
                onChange={handleChange}
                value={formData.fullName}
                required
                aria-required="true"
                aria-invalid={!!errors.fullName}
                aria-describedby={errors.fullName ? 'fullName-error' : undefined}
              />
            </div>

            {errors.fullName && (
              <span id="fullName-error" role="alert" className="md:text-md text-sm text-red-600">
                {errors.fullName}
              </span>
            )}
          </div>

          <div className="gap-4 flex w-full flex-col">
            <label className="text-xs md:text-sm block uppercase" htmlFor="email">
              Email
            </label>

            <div className="stonerwhitebg rounded-lg shadow-lg">
              <input
                id="email"
                type="email"
                className="md:text-md rounded-lg p-4 text-sm w-full transition-all duration-150 ease-linear focus:ring focus:outline-none"
                placeholder="Email"
                name="email"
                onChange={handleChange}
                value={formData.email}
                required
                aria-required="true"
                aria-invalid={!!errors.email}
                aria-describedby={errors.email ? 'email-error' : undefined}
              />
            </div>

            {errors.email && (
              <span id="email-error" role="alert" className="md:text-md text-sm text-red-600">
                {errors.email}
              </span>
            )}
          </div>

          <div className="gap-4 flex w-full flex-col">
            <label className="text-xs md:text-sm block uppercase" htmlFor="message">
              Message
            </label>

            <div className="stonerwhitebg rounded-lg shadow-lg">
              <textarea
                id="message"
                rows={4}
                cols={80}
                className="md:text-md rounded-lg p-4 text-sm w-full transition-all duration-150 ease-linear focus:ring focus:outline-none"
                placeholder="Votre message ..."
                name="message"
                onChange={handleChange}
                value={formData.message}
                required
                aria-required="true"
                aria-invalid={!!errors.message}
                aria-describedby={errors.message ? 'message-error' : undefined}
              />
            </div>

            {errors.message && (
              <div aria-live="assertive">
                <span id="message-error" role="alert" className="md:text-md text-sm text-red-600">
                  {errors.message}
                </span>
              </div>
            )}
          </div>

          <fieldset
            className="gap-4 flex w-full flex-col"
            aria-describedby={errors.agreed ? 'consent-error' : undefined}
          >
            <legend className="sr-only">Consentement</legend>

            <label className="md:text-md gap-2 text-sm flex cursor-pointer items-center">
              <input
                type="checkbox"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
                className="focus:ring-moonstonebluebg h-10 w-10 rounded border-red-500 focus:ring-2"
                aria-describedby={errors.agreed ? 'consent-error' : undefined}
                aria-label="Consentement au traitement des données personnelles"
              />
              <span>
                En soumettant ce formulaire, j&apos;accepte que mes informations soient utilisées
                par Adaequatio pour me contacter dans le cadre de cette demande.
              </span>
            </label>

            {errors.agreed && (
              <span id="consent-error" role="alert" className="md:text-md text-sm text-red-600">
                {errors.agreed}
              </span>
            )}
          </fieldset>

          <div className="mt-3 text-center">
            <button
              className="policebluebg text-stonerwhite md:text-md rounded-lg px-8 py-4 text-sm shadow hover:shadow-lg cursor-pointer uppercase transition-all duration-150 ease-linear outline-none focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
              type="submit"
              disabled={isLoading || !agreed}
              aria-disabled={isLoading || !agreed}
              aria-busy={isLoading}
            >
              {isLoading ? 'Sending...' : 'Envoyer'}
            </button>
          </div>
        </form>
      </>
    );
  };

  return (
    <section
      id="contact-section"
      className="policebluebg p-4 sm:p-8 md:p-10 lg:p-12"
      aria-label="Formulaire de contact pour entrer en relation avec Adaequatio"
    >
      <div className="max-w-6xl m-auto flex justify-center">
        <div className="stonerwhitebg space-y-4 rounded-lg p-4 shadow-lg md:p-6 lg:w-[50%] lg:p-10 xl:p-12 w-full grow">
          <h2>Prenez part à la révolution de l&apos;emploi !</h2>

          <div className="py-2">
            <hr />
          </div>

          {contactForm()}
        </div>
      </div>
    </section>
  );
}
